/**
 * Session-policy overrides admin API module — the location side of the
 * Time & Attendance account-default / location-value settings, the geofence
 * centre endpoints, and the account-wide geofence health summary.
 *
 *   GET    /accounts/{a}/locations/{l}/session-policy   → flat effective fields + sources/overrides/account/place/enforced
 *   PATCH  …/session-policy                               → tri-state body (null = follow the account); returns the envelope
 *   DELETE …/session-policy                               → follow the account everywhere; returns the envelope
 *   GET    /locations/{l}/geofence                        → { location_id, account_id, status, place|null }
 *   POST   /locations/{l}/geofence/recalculate            → geocode the location address now (400 LOCATION_ADDRESS_REQUIRED)
 *   PUT    /locations/{l}/geofence/centre                 → manual pin (status verified); DELETE → unset
 *   GET    /accounts/{a}/geofence-health                  → counts + the non-enforced locations
 *
 * The account row keeps the existing session-policies endpoints (usersRoles.ts);
 * its lat/lng are ignored by the backend now — the centre lives on the location.
 */
import type { GeofenceNotEnforcedReason, GeofencePlace, GeofencePlaceStatus } from '@turn-connect/shared';
import { normalizeGeofencePlace } from '@turn-connect/shared';
import { webApiClient } from '../apiClient';
import { dedup } from './helpers';
import { POLICY_FIELD_LOCKED, POLICY_FIELD_LOCKED_MESSAGE } from './policyErrors';
import {
  normalizeSessionEnvelope,
  toWirePatch,
  type SessionPolicyEnvelope,
  type SessionPolicyPatch,
} from '../../components/policy/sessionPolicyEnvelope';

export interface LocationGeofence {
  location_id: string;
  account_id: string;
  status: GeofencePlaceStatus;
  place: GeofencePlace | null;
}

export interface GeofenceHealthLocation {
  location_id: string;
  name: string;
  status: GeofencePlaceStatus;
  reason: GeofenceNotEnforcedReason | string;
}

export interface GeofenceHealth {
  total: number;
  enforced: number;
  disabled: number;
  unset: number;
  unverified: number;
  failed: number;
  review: number;
  pending: number;
  /** The non-enforced locations. */
  locations: GeofenceHealthLocation[];
}

export const LOCATION_ADDRESS_REQUIRED = 'LOCATION_ADDRESS_REQUIRED';

/** Carries the backend's error code so callers can branch on it (the ApiClient flattens it into errorBody). */
export class SessionPolicyApiError extends Error {
  code?: string;
  status?: number;
  constructor(message: string, code?: string, status?: number) {
    super(message);
    this.name = 'SessionPolicyApiError';
    this.code = code;
    this.status = status;
  }
}

function fail(res: { error?: string; errorBody?: any; status?: number }, fallback: string): never {
  const eb: any = res.errorBody;
  const code: string | undefined = eb?.code;
  // A location write on a field the account locked: say who owns it.
  const message = code === POLICY_FIELD_LOCKED ? POLICY_FIELD_LOCKED_MESSAGE : (eb && (eb.error || eb.message)) || res.error || fallback;
  throw new SessionPolicyApiError(message, code, res.status);
}

const policyPath = (accountId: string, locationId: string) => `/accounts/${accountId}/locations/${locationId}/session-policy`;
const geofencePath = (locationId: string) => `/locations/${locationId}/geofence`;

export async function getLocationSessionPolicy(accountId: string, locationId: string): Promise<SessionPolicyEnvelope> {
  return dedup(`getLocationSessionPolicy:${accountId}:${locationId}`, async () => {
    const res = await webApiClient.get<Record<string, any>>(policyPath(accountId, locationId));
    if (!res.success) fail(res, 'Failed to load the time & attendance settings');
    return normalizeSessionEnvelope(res.data ?? {});
  });
}

export async function updateLocationSessionPolicy(
  accountId: string,
  locationId: string,
  patch: SessionPolicyPatch
): Promise<SessionPolicyEnvelope> {
  const res = await webApiClient.patch<Record<string, any>>(policyPath(accountId, locationId), toWirePatch(patch));
  if (!res.success) fail(res, 'Failed to save the time & attendance settings');
  return normalizeSessionEnvelope(res.data ?? {});
}

/** Follow the account everywhere. */
export async function resetLocationSessionPolicy(accountId: string, locationId: string): Promise<SessionPolicyEnvelope> {
  const res = await webApiClient.delete<Record<string, any>>(policyPath(accountId, locationId));
  if (!res.success) fail(res, 'Failed to reset the time & attendance settings');
  return normalizeSessionEnvelope(res.data ?? {});
}

export async function getLocationGeofence(locationId: string): Promise<LocationGeofence> {
  return dedup(`getLocationGeofence:${locationId}`, async () => {
    const res = await webApiClient.get<LocationGeofence>(geofencePath(locationId));
    if (!res.success) fail(res, 'Failed to load the geofence centre');
    return { ...(res.data as LocationGeofence), place: normalizeGeofencePlace(res.data?.place) };
  });
}

/** Geocode the location address now. Throws SessionPolicyApiError code LOCATION_ADDRESS_REQUIRED when there is no address. */
export async function recalculateLocationGeofence(locationId: string): Promise<LocationGeofence> {
  const res = await webApiClient.post<LocationGeofence>(`${geofencePath(locationId)}/recalculate`, {});
  if (!res.success) fail(res, 'Could not recalculate the geofence centre');
  return { ...(res.data as LocationGeofence), place: normalizeGeofencePlace(res.data?.place) };
}

export async function pinLocationGeofenceCentre(
  locationId: string,
  centre: { latitude: number; longitude: number; address?: string }
): Promise<LocationGeofence> {
  const res = await webApiClient.put<LocationGeofence>(`${geofencePath(locationId)}/centre`, centre);
  if (!res.success) fail(res, 'Could not pin the geofence centre');
  return { ...(res.data as LocationGeofence), place: normalizeGeofencePlace(res.data?.place) };
}

export async function clearLocationGeofenceCentre(locationId: string): Promise<void> {
  const res = await webApiClient.delete<void>(`${geofencePath(locationId)}/centre`);
  if (!res.success) fail(res, 'Could not clear the pinned centre');
}

export async function getAccountGeofenceHealth(accountId: string): Promise<GeofenceHealth> {
  return dedup(`getAccountGeofenceHealth:${accountId}`, async () => {
    const res = await webApiClient.get<GeofenceHealth>(`/accounts/${accountId}/geofence-health`);
    if (!res.success) fail(res, 'Failed to load the geofence summary');
    const d = res.data;
    return {
      total: d?.total ?? 0,
      enforced: d?.enforced ?? 0,
      disabled: d?.disabled ?? 0,
      unset: d?.unset ?? 0,
      unverified: d?.unverified ?? 0,
      failed: d?.failed ?? 0,
      review: d?.review ?? 0,
      pending: d?.pending ?? 0,
      // nil slice → null from Go.
      locations: Array.isArray(d?.locations) ? d.locations : [],
    };
  });
}

export const sessionPolicyOverridesAdmin = {
  getLocationSessionPolicy,
  updateLocationSessionPolicy,
  resetLocationSessionPolicy,
  getLocationGeofence,
  recalculateLocationGeofence,
  pinLocationGeofenceCentre,
  clearLocationGeofenceCentre,
  getAccountGeofenceHealth,
};
